import { useEffect } from 'react';
import { clearCache, useRequest } from 'ahooks';
import { subscribeTag } from './registry';
/**
 * 触发层的频率收敛，throttle / debounce 二选一（字段与 ahooks `useRequest`
 * 同名同义，原样透传；同设两者时 ahooks 两个插件都包 runAsync，行为未定义）。
 * 与 `http.ts` 的全站 1.5s 网络节流分工：那层合并的是 fetch 发送次数，
 * 这层收敛的是 service 调用与 loading 翻转 —— SSE 事件风暴里前者省网络，
 * 后者止住列表页的加载抖动。
 *
 * 语义锚点（lodash throttle/debounce，ahooks 3.10 实测）：
 * - trailing 默认开：窗口内最后一次触发必在窗口末尾补发一次，且用的是
 *   最新一次的 fetcher 闭包 —— 「最后再请求一次」有保证。
 * - `throttleTrailing: false` 是显式丢弃补发（只保留首飞），别误设。
 * - 卸载会取消未到点的补发：不会在组件卸载后补枪。
 */
export type RateLimit =
  | { throttleWait: number; throttleLeading?: boolean; throttleTrailing?: boolean }
  | { debounceWait: number; debounceLeading?: boolean; debounceTrailing?: boolean };
export interface UseQueryOptions<TData> {
  key: string;
  tags?: readonly string[];
  ready?: boolean;
  staleTime?: number;
  pollingInterval?: number;
  refreshDeps?: readonly unknown[];
  rate?: RateLimit;
  onSuccess?: (data: TData) => void;
}
export function useQuery<TData>(fetcher: () => Promise<TData>, options: UseQueryOptions<TData>) {
  const { key, tags, ready = true, staleTime = 0, pollingInterval = 0, onSuccess } = options;
  const tagKey = tags !== undefined && tags.length > 0 ? tags.join('|') : '';
  const requestOptions = {
    cacheKey: key,
    staleTime,
    ready,
    refreshDeps: [key, ...(options.refreshDeps ?? [])],
    ...(pollingInterval > 0 ? { pollingInterval } : {}),
    ...(onSuccess === undefined ? {} : { onSuccess }),
    ...(options.rate ?? {}),
  };
  const result = useRequest(fetcher, requestOptions as never) as {
    data: TData | undefined;
    error: unknown;
    loading: boolean;
    refresh: () => void;
  };
  const { refresh } = result;
  useEffect(() => {
    if (tagKey === '') return;
    const onInvalidate = () => {
      clearCache(key);
      refresh();
    };
    const unsubscribes = tagKey.split('|').map((tag) => subscribeTag(tag, onInvalidate));
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [tagKey, key, refresh]);
  return result;
}
